/**
 * Contact form topic dropdown (sections/contact-main.liquid).
 * On desktop (mouse) the browser's own dropdown opens over the field. This swaps it for a list that always
 * opens below the field. The real <select> stays in the form (hidden), so submitting, the "Route" line
 * (assets/contact-route.js) and the ?topic= preselect all keep working. Phones and tablets keep the native
 * picker, which suits touch screens.
 */
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  const select = document.getElementById('ContactTopic');
  if (!select || select.dataset.enhanced) return;
  select.dataset.enhanced = 'true';

  const wrap = document.createElement('div');
  wrap.className = 'csel';
  select.parentNode.insertBefore(wrap, select);
  wrap.appendChild(select);
  select.classList.add('csel__native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'contact-form__input contact-form__select csel__button';
  button.id = 'ContactTopicButton';
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');
  const label = document.querySelector('label[for="ContactTopic"]');
  if (label) {
    label.htmlFor = button.id;
    if (!label.id) label.id = 'ContactTopicLabel';
    button.setAttribute('aria-labelledby', label.id);
  }

  const list = document.createElement('ul');
  list.className = 'csel__list';
  list.setAttribute('role', 'listbox');
  list.id = 'ContactTopicList';
  list.hidden = true;
  button.setAttribute('aria-controls', list.id);

  const items = [...select.options].map((option, i) => {
    const li = document.createElement('li');
    li.className = 'csel__option';
    li.id = `ContactTopicOption${i}`;
    li.setAttribute('role', 'option');
    li.textContent = option.textContent;
    li.addEventListener('mousedown', (event) => event.preventDefault());
    li.addEventListener('click', () => {
      choose(i);
      close();
      button.focus();
    });
    li.addEventListener('mousemove', () => setActive(i));
    list.appendChild(li);
    return li;
  });
  wrap.append(button, list);

  let active = select.selectedIndex;
  let typed = '';
  let typedTimer;

  const paint = () => {
    button.textContent = select.options[select.selectedIndex]?.textContent || '';
    items.forEach((li, i) => li.setAttribute('aria-selected', String(i === select.selectedIndex)));
  };
  const setActive = (i) => {
    active = Math.max(0, Math.min(items.length - 1, i));
    items.forEach((li, n) => li.classList.toggle('is-active', n === active));
    button.setAttribute('aria-activedescendant', items[active].id);
    items[active].scrollIntoView({ block: 'nearest' });
  };
  const choose = (i) => {
    if (select.selectedIndex !== i) {
      select.selectedIndex = i;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    paint();
  };
  const open = () => {
    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    setActive(select.selectedIndex);
  };
  const close = () => {
    list.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    button.removeAttribute('aria-activedescendant');
  };
  const isOpen = () => !list.hidden;

  button.addEventListener('click', () => (isOpen() ? close() : open()));
  button.addEventListener('blur', close);
  button.addEventListener('keydown', (event) => {
    const { key } = event;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen()) open();
      else setActive(active + (key === 'ArrowDown' ? 1 : -1));
    } else if (key === 'Home' || key === 'End') {
      event.preventDefault();
      if (!isOpen()) open();
      setActive(key === 'Home' ? 0 : items.length - 1);
    } else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      if (!isOpen()) return open();
      choose(active);
      close();
    } else if (key === 'Escape' && isOpen()) {
      event.preventDefault();
      close();
    } else if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      // Type a few letters to jump to a matching option.
      typed += key.toLowerCase();
      clearTimeout(typedTimer);
      typedTimer = setTimeout(() => (typed = ''), 600);
      const hit = items.findIndex((li) => li.textContent.trim().toLowerCase().startsWith(typed));
      if (hit > -1) {
        if (!isOpen()) open();
        setActive(hit);
      }
    }
  });
  // The native select can still be changed from outside (for example by the ?topic= preselect).
  select.addEventListener('change', paint);
  paint();
})();
